// react
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

// api
import { useUnitsDeleteAsyncByidMutation } from '@/api/generated/recalc/endpoints/v1/units';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

interface Unit {
  UnitId: number;
  Name?: string;
}

interface DeleteUnitDialogProps {
  open: boolean;
  unit: Unit | null;
  onClose: () => void;
  /** Called after a successful delete, e.g. to navigate away. */
  onDeleted?: (unit: Unit) => void;
}

const DeleteUnitDialog = ({
  open,
  unit,
  onClose,
  onDeleted,
}: DeleteUnitDialogProps) => {
  const { t } = useTranslation('units-gateways-sensors');
  const dispatch = useDispatch();
  const [deleteUnit, { isLoading: isDeleting }] =
    useUnitsDeleteAsyncByidMutation();

  const handleConfirm = async () => {
    if (!unit?.UnitId) return;

    const result = await deleteUnit({ id: unit.UnitId });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message:
            t('units.unitDeletedSuccess', {
              name: unit.Name || unit.UnitId,
            }) || `Unit "${unit.Name || unit.UnitId}" deleted successfully`,
          severity: 'success',
        }),
      );

      onClose();
      onDeleted?.(unit);
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('units.deleteUnit', { defaultValue: 'Delete Unit' })}
      </DialogTitle>
      <DialogContent dividers>
        <Typography>
          {t('units.deleteConfirmation', {
            defaultValue:
              'Are you sure you want to delete this unit? This action cannot be undone.',
          })}
        </Typography>
        <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
          {unit?.Name || unit?.UnitId}
        </Typography>
        {unit?.UnitId && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('units.unitId', { defaultValue: 'Unit ID' })}: {unit.UnitId}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isDeleting}>
          {t('common.cancel', { defaultValue: 'Cancel' })}
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={isDeleting}
          data-testid="delete-dialog-confirm-btn"
        >
          {isDeleting
            ? t('common.deleting', { defaultValue: 'Deleting...' })
            : t('common.delete', { defaultValue: 'Delete' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteUnitDialog;
